import { createSelector } from 'reselect';
import { FileUploadInfo, UploadState, LegalDocumentType, AttachmentUploadInfo } from './types';
import { actionKey } from './constants';

function getSlice(state): UploadState {
  return (
    state[actionKey] || {
      fileUploads: [],
      fileUploadsWaiting: [],
      fileUploadsCompleted: [],
      documentUploads: {},
    }
  );
}

export const getUploads = createSelector(getSlice, (slice): FileUploadInfo[] => slice.fileUploads);

export const getUploadsWaiting = createSelector(
  getSlice,
  (slice): FileUploadInfo[] => slice.fileUploadsWaiting
);

export const getAllUploads = createSelector(
  getUploads,
  getUploadsWaiting,
  (uploads, uploadsWaiting): FileUploadInfo[] => [...uploads, ...uploadsWaiting]
);

export const getCompletedUploads = createSelector(
  getSlice,
  (slice): FileUploadInfo[] => slice.fileUploadsCompleted
);

export const getUploadById = createSelector(
  getAllUploads,
  (state, uploadId: number) => uploadId,
  (allUploads, uploadId): FileUploadInfo | undefined =>
    allUploads.find(fu => fu.uploadId === uploadId)
);

export const getUploadsByTag = createSelector(
  getAllUploads,
  (state, tag: string | undefined) => tag,
  (allUploads, tag): FileUploadInfo[] => allUploads.filter(fu => fu.tag === tag)
);

export const getAttachmentUploads = createSelector(
  getSlice,
  (slice): AttachmentUploadInfo[] => slice.attachmentUploads
);

export const getAttachmentUploadsWaiting = createSelector(
  getSlice,
  (slice): AttachmentUploadInfo[] => slice.attachmentUploadsWaiting
);

export const getAllAttachmentUploads = createSelector(
  getAttachmentUploads,
  getAttachmentUploadsWaiting,
  (attachmentUploads, attachmentUploadsWaiting): AttachmentUploadInfo[] => [
    ...attachmentUploads,
    ...attachmentUploadsWaiting,
  ]
);

export const getAttachmentUploadsByTag = createSelector(
  getAllAttachmentUploads,
  (state, tag: string | undefined) => tag,
  (attachmentUploads, tag): AttachmentUploadInfo[] => attachmentUploads.filter(fu => fu.tag === tag)
);

export const getDocumentUploads = createSelector(getSlice, slice => slice.documentUploads);

export const getDocumentUploadByType = createSelector(
  getDocumentUploads,
  (state, documentType: LegalDocumentType) => documentType,
  (documentUploads, documentType) => documentUploads[documentType]
);

export const getAllAttachmentDeviationLockStatuses = createSelector(
  getSlice,
  (slice): Record<number, boolean> => slice.attachmentDeviationLocked
);

export const getIsAttachmentDeviationLocked = createSelector(
  getAllAttachmentDeviationLockStatuses,
  (state, deviationId: number): number => deviationId,
  (slice, deviationId: number): boolean => slice?.[deviationId] === true
);
